import { useRef, useEffect } from 'react';

// Shared canvas-based confetti. Replaces the old per-board DOM confetti (80–90
// individually-animated <div> compositor layers), which stuttered badly on weak
// TV GPUs. A single <canvas> drawn via requestAnimationFrame composites one
// surface instead of ~90 layers, giving smooth motion on the Fire TV while
// staying rich in a desktop browser. It's a one-shot burst: particles fall once,
// then the animation loop stops to free the GPU.

const DEFAULT_COLORS = ['#FFD700', '#19C3C8', '#FFFFFF', '#EF4444', '#10B981', '#A78BFA', '#FF8C00'];

export default function Confetti({ colors = DEFAULT_COLORS }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const params = new URLSearchParams(window.location.search);
    const lite = params.get('player') === 'android_tv' || params.get('lite') === '1' || params.get('lite') === 'true';

    // Cap the canvas backing-store resolution — weak TV GPUs are fill-rate bound.
    const dpr = lite ? 1 : Math.min(window.devicePixelRatio || 1, 2);
    const N   = lite ? 80 : 150;

    let w = 0, h = 0;
    const resize = () => {
      w = canvas.clientWidth || window.innerWidth;
      h = canvas.clientHeight || window.innerHeight;
      canvas.width  = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const rand = (a, b) => a + Math.random() * (b - a);
    // Stagger the start by spreading initial Y well above the top so pieces enter
    // over a couple of seconds rather than all at once.
    const parts = Array.from({ length: N }, () => ({
      x: rand(0, w), y: rand(-h * 2.5, 0),
      r: rand(3, 8),
      vy: rand(80, 200),                 // fall speed px/sec
      vx: rand(-30, 30),
      rot: rand(0, Math.PI * 2), vrot: rand(-5, 5),
      color: colors[(Math.random() * colors.length) | 0],
      rect: Math.random() < 0.55,
      wob: rand(0, Math.PI * 2), vwob: rand(2, 5), wobAmp: rand(0.3, 1.1),
      done: false,
    }));

    let raf = 0, last = performance.now(), running = true;
    const tick = (now) => {
      if (!running) return;
      const dt = Math.min((now - last) / 1000, 0.05); // clamp to avoid big jumps
      last = now;
      ctx.clearRect(0, 0, w, h);
      let active = false;
      for (const p of parts) {
        if (p.done) continue;
        p.y   += p.vy * dt;
        p.wob += p.vwob * dt;
        p.x   += p.vx * dt + Math.sin(p.wob) * p.wobAmp;
        p.rot += p.vrot * dt;
        if (p.y > h + 14) { p.done = true; continue; }
        active = true;
        if (p.y < -14) continue; // not on screen yet
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.rect) ctx.fillRect(-p.r, -p.r * 0.6, p.r * 2, p.r * 1.2);
        else { ctx.beginPath(); ctx.arc(0, 0, p.r, 0, Math.PI * 2); ctx.fill(); }
        ctx.restore();
      }
      if (active) { raf = requestAnimationFrame(tick); }
      else { running = false; ctx.clearRect(0, 0, w, h); }
    };
    raf = requestAnimationFrame(tick);

    return () => { running = false; cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, [colors]);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 300 }}
    />
  );
}
